/* eslint-disable consistent-return */
/* eslint-disable @typescript-eslint/no-empty-function */
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import React, { createContext, FC, useState, useEffect } from 'react';
import useFetchState from '../hooks/useFetchState';
import { IReturnLabel } from '../models/returns';
import {
  IErrorOMSService,
  IErrorReturnService,
  IPrintLabelError,
} from '../models/returns/returns-common';
import { buildReturnLabelRequest } from '../utils/returns/returns-helper';
import {
  createHtmlContent,
  getErrorBySSRCode,
  openNativeAppLabel,
  openNewWindow,
} from '../utils/returns/return-label';
import { useExperienceContext } from '../hooks/useContexts';
import { GetReturnShippingLabelAPI } from '../utils/returns/api-helpers';
import { pushErrorEvent } from '../utils/analytics';

const PRINT_LABEL_ERROR_MESSAGES: Record<IPrintLabelError['type'], string> = {
  TRYAGAIN:
    "We're sorry. There was a temporary technical error. Please try again shortly. If the issue persists, please call Customer Support at 1-800-430-3376.",
  USED: 'This shipping label has already been used. Please call Customer Support at 1-800-430-3376 if you have questions or need additional help.',
  EXPIRED:
    'This shipping label has expired. Please call Customer Support at 1-800-430-3376 if you have questions or need additional help.',
  UNKNOWN:
    'We’re sorry. There was a temporary technical error. Please try again shortly. If the problem persists, please call Customer Support at 1-800-430-3376.',
  TEMPORARY:
    'This shipping label cannot be printed at this time. Please call Customer Support at 1-800-430-3376 if you have questions or need additional help.',
  SWITCHEDTOPICKUP:
    'This label is no longer valid as the return is set to be picked up by UPS. Please call Customer Support at 1-800-430-3376 if you have questions or need additional help.',
};

const UPS_BARCODE_ERROR_MESSAGE =
  'We had a technical issue and couldn’t generate your barcode. Check again later or look for the barcode in your return instructions email.';

const getReturnLabelErrorCode = (
  error: IErrorReturnService | IErrorOMSService | Error | null,
) => (error && 'code' in error ? error.code : 'n/a');

const trackReturnLabelError = (
  type: ButtonClickedType,
  errorInfo: IPrintLabelError,
  errorCode = 'n/a',
) => {
  const isBarcode = type === 'barcode';
  pushErrorEvent(
    isBarcode ? 'view ups barcode' : 'print return label',
    errorCode,
    isBarcode
      ? UPS_BARCODE_ERROR_MESSAGE
      : PRINT_LABEL_ERROR_MESSAGES[errorInfo.type],
  );
};

interface IReturnLabelProvider {
  children: React.ReactNode;
  orderId: string;
  emailId: string;
  trackingNumbers: string[];
  isGuest: boolean;
  net: string | undefined;
  xAuthGuestToken?: string;
}

type ButtonClickedType = 'barcode' | 'label' | '';

export interface IReturnLabelContext {
  orderId: string;
  loading: boolean;
  fetchReturnLabel: (
    type: ButtonClickedType,
    openWindow?: boolean,
  ) => Promise<void>;
  returnLabelResponse: IReturnLabel[] | null;
  error: IErrorReturnService | IErrorOMSService | Error | null;
  apiError: IPrintLabelError | undefined;
  setAPIError: (error: IPrintLabelError | undefined) => void;
  retryAvailable: boolean;
  setRetryAvailable: (value: boolean) => void;
  openNewReturnLabelWindow: () => void;
  lastClicked: ButtonClickedType;
}

const defaultState: IReturnLabelContext = {
  orderId: '',
  loading: false,
  error: null,
  lastClicked: '',
  fetchReturnLabel: async () => {},
  returnLabelResponse: null,
  apiError: undefined,
  setAPIError: () => {},
  retryAvailable: true,
  setRetryAvailable: () => {},
  openNewReturnLabelWindow: () => {},
};

export const ReturnLabelContext =
  createContext<IReturnLabelContext>(defaultState);

export const ReturnLabelProvider: FC<IReturnLabelProvider> = ({
  children,
  orderId,
  emailId,
  trackingNumbers,
  isGuest,
  net,
  xAuthGuestToken,
}) => {
  const {
    loading,
    fetchData,
    data: returnLabelResponse,
    error,
  } = useFetchState<IReturnLabel[]>();
  const [retryAvailable, setRetryAvailable] = useState(true);
  const [apiError, setAPIError] = useState<IPrintLabelError | undefined>();
  const [lastClicked, setLastClicked] = useState<ButtonClickedType>('');
  const { isConsumerApp } = useExperienceContext();
  const { userID } = useThdCustomer();
  const isNativeApp = isConsumerApp || false;

  const request = buildReturnLabelRequest(
    orderId,
    emailId,
    trackingNumbers,
    isGuest,
    net,
  );

  const fetchReturnLabel = async (
    type: ButtonClickedType,
    openWindow?: boolean,
  ) => {
    setLastClicked(type);
    setAPIError(undefined);
    let labelWindow: Window | null = null;
    if (!isNativeApp && openWindow) {
      labelWindow = openNewReturnLabelWindow();
      if (labelWindow === null) return;
    }

    if (!returnLabelResponse) {
      const { data, error: fetchError } = await fetchData(() =>
        GetReturnShippingLabelAPI(request, userID, xAuthGuestToken),
      );
      if (data) {
        setRetryAvailable(true);
        if (type === 'barcode') return;
        const htmlLabel = createHtmlContent(data, orderId);
        if (isNativeApp) {
          return openNativeAppLabel(htmlLabel);
        }
        if (labelWindow) {
          return labelWindow.document.write(htmlLabel || '');
        }
      }
      // on failure
      if (labelWindow) {
        labelWindow.close();
      }
      if (fetchError) {
        setRetryAvailable(false);
        if ('code' in fetchError) {
          const errorInfo = getErrorBySSRCode(
            fetchError as IErrorReturnService,
          );
          setAPIError(errorInfo);
          trackReturnLabelError(
            type,
            errorInfo,
            getReturnLabelErrorCode(fetchError),
          );
          return;
        }
        const errorInfo: IPrintLabelError = {
          type: 'TRYAGAIN',
          retryable: true,
        };
        setAPIError(errorInfo);
        trackReturnLabelError(type, errorInfo);
      }
    } else {
      setRetryAvailable(true);
      if (type === 'barcode') return;
      const htmlLabel = createHtmlContent(returnLabelResponse, orderId);
      if (isNativeApp) {
        return openNativeAppLabel(htmlLabel);
      }
      if (labelWindow) {
        return labelWindow.document.write(htmlLabel || '');
      }
    }
  };

  const openNewReturnLabelWindow = () => {
    const newWindow = openNewWindow();
    if (!newWindow) {
      const errorInfo: IPrintLabelError = { type: 'TRYAGAIN', retryable: true };
      setAPIError(errorInfo);
      trackReturnLabelError('label', errorInfo);
      return null;
    }
    return newWindow;
  };

  useEffect(() => {
    if (apiError?.retryable) {
      const timeoutId = setTimeout(() => {
        setRetryAvailable(true);
      }, 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [apiError?.retryable, retryAvailable]);

  return (
    <ReturnLabelContext.Provider
      value={{
        orderId,
        loading,
        lastClicked,
        fetchReturnLabel,
        returnLabelResponse,
        apiError,
        setAPIError,
        error,
        retryAvailable,
        setRetryAvailable,
        openNewReturnLabelWindow,
      }}
    >
      {children}
    </ReturnLabelContext.Provider>
  );
};
